import React from 'react';

import { useQueryMobileAppStatisticsHook } from '@fd/customHooks/useQueryMobileAppStatisticsHook';
import { Avatar, Grid, makeStyles, Theme } from '@material-ui/core';
import { Language } from '@material-ui/icons';
import moment from 'moment';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';

import { DashboardCard, Typography } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';
import { FailedResponseMessage } from '@fd/ui/molecules/FailedResponseMessage';

import { flagService } from '../../../../../services';
import { AppDownloadsCard } from '../../components/AppDownloadsCard';
import { AppInformation } from '../../components/AppInformation';
import { AppRatingCard } from '../../components/AppRatingCard';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
  dashboardCardContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  gridItem: {
    display: 'flex',
  },
  sectionContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  version: {
    fontSize: '1.5rem',
    fontWeight: 400,
  },
  versionIconBackground: {
    backgroundColor: '#eaf2ff',
    height: '60px',
    width: '60px',
  },
  versionIcon: {
    color: '#9caed4',
    height: '33px',
    width: '33px',
  },
}));

const Android = (props: MappedState) => {
  const { appId, isMobileAppsOn, translate } = props;
  const classes = useStyles();

  const { data, isLoading, isError, refetch } = useQueryMobileAppStatisticsHook(
    appId,
    ['Android'],
    isMobileAppsOn || false
  );

  return (
    <PageLayout
      documentTitle="Android"
      title={<Translate id="Android" />}
      toParent={`/${appId}/sales-channels/mobile-apps`}
    >
      <Permissions allowed={['ViewSalesChannels', 'EditSalesChannels']}>
        {isError ? (
          <FailedResponseMessage
            description="MobileApps_appDetails_responseErrorMessage"
            onRetry={() => refetch()}
            title="Error_fetching_app_details"
          />
        ) : (
          <div className={classes.container}>
            <Grid className={classes.sectionContainer} container spacing={3} direction="row">
              <Grid className={classes.gridItem} item xs={12}>
                <AppDownloadsCard
                  isLoading={isLoading}
                  downloads={data?.Data[0].NumberDownload}
                  translate={translate}
                />
              </Grid>
              <Grid className={classes.gridItem} item xs={12} sm={6}>
                <DashboardCard isLoading={isLoading} title={translate('Current_version') as string}>
                  <div className={classes.dashboardCardContainer}>
                    <Typography className={classes.version} variant="body1">
                      {data?.Data[0].CurrentVersion}
                    </Typography>
                    <Avatar className={classes.versionIconBackground}>
                      <Language className={classes.versionIcon} />
                    </Avatar>
                  </div>
                </DashboardCard>
              </Grid>
              <Grid className={classes.gridItem} item xs={12} sm={6}>
                <AppRatingCard
                  isLoading={isLoading}
                  rating={data?.Data[0].CurrentRate}
                  translate={translate}
                />
              </Grid>
            </Grid>
            <AppInformation
              isLoading={isLoading}
              isPublished={data?.Data[0].Published}
              appBundleId={data?.Data[0].AppBundleId as string}
              lastUpdated={moment(data?.Data[0].LastUpdated).format('DD.MM.YYYY')}
              releaseNotes={data?.Data[0].Notes}
              storeLink={data?.Data[0].PlayStoreLink as string}
              translate={translate}
            />
          </div>
        )}
      </Permissions>
    </PageLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { currentApp } = state;
  return {
    appId: currentApp.AppId as string,
    isMobileAppsOn: flagService.isFlagOn(state, 'mobileApps'),
    translate: getTranslate(state.locale),
  };
};

const EnhancedComponent = connect(mapStateToProps)(Android);
export { EnhancedComponent as Android };
